const ACTIONS_KEY = '_actions';
const TASKS_KEY = '_tasks';
const WORKFLOW_KEY = '_workflows';
const CURRENT_KEY = '_current';

export enum Operation {
    ACTION = ACTIONS_KEY,
    TASK = TASKS_KEY,
    WORKFLOW = WORKFLOW_KEY,
}

export class Persistence {
    public static _setCurrent(type: Operation, id: string) {
        try {
            if (!type || !id) {
                console.warn('Invalid type or id provided to _setCurrent');
                return;
            }
            const storage = JSON.parse(localStorage.getItem(CURRENT_KEY) || '{}');
            storage[type] = id;
            localStorage.setItem(CURRENT_KEY, JSON.stringify(storage));
        } catch (e) {
            console.error(`Error setting current storage for type ${type}:`, e);
        }
    }

    public static getCurrent(type: Operation = Operation.WORKFLOW) {
        try {
            const currents = localStorage.getItem(CURRENT_KEY) || '{}';
            const parsed = JSON.parse(currents);
            return parsed[type] || null;
        } catch (e) {
            console.error(`Error parsing current storage:`, e);
            return null;
        }
    }
    public static getAll(type: Operation) {
        try {
            if (!type) {
                console.warn('Invalid type provided to getAll');
                return {};
            }
            const item = localStorage.getItem(type) || '{}';
            return JSON.parse(item);
        } catch (e) {
            console.error(`Error parsing storage for type ${type}:`, e);
            return {};
        }
    }

    public static setAll(type: Operation, data: object) {
        try {
            if (!type || !data) {
                console.warn('Invalid type or data provided to setAll');
                return;
            }
            const storage = JSON.stringify(data);
            localStorage.setItem(type, storage);
        } catch (e) {
            console.error(`Error saving to storage for type ${type}:`, e);
        }
    }

    public static getById(type: Operation, id: string) {
        try {
            if (!type || !id) {
                console.warn('Invalid type or id provided to getById');
                return null;
            }
            const storage = Persistence.getAll(type);
            return storage[id] || null;
        } catch (e) {
            console.error(`Error getting item by id ${id} for type ${type}:`, e);
            return null;
        }
    }

    public static save(type: Operation, id: string, data: Record<string, any>) {
        try {
            if (!type || !id || !data) {
                console.warn('Invalid type, id, or data provided to save');
                return;
            }
            const storage = Persistence.getAll(type);
            storage[id] = data;
            Persistence.setAll(type, storage);
        } catch (e) {
            console.error(`Error saving data for id ${id} and type ${type}:`, e);
        }
    }

    public static delete(type: Operation, id: string) {
        try {
            if (!type || !id) {
                console.warn('Invalid type or id provided to delete');
                return;
            }
            Persistence.deleteMany(type, [id]);
        } catch (e) {
            console.error(`Error deleting item with id ${id} for type ${type}:`, e);
        }
    }

    public static deleteMany(type: Operation, ids: string[]) {
        try {
            if (!type || !ids?.length) {
                console.warn('Invalid type or ids provided to deleteMany');
                return;
            }
            const storage = Persistence.getAll(type);
            ids.forEach((id) => {
                if (id) {
                    delete storage[id];
                }
            });
            Persistence.setAll(type, storage);
        } catch (e) {
            console.error(`Error deleting multiple items for type ${type}:`, e);
        }
    }

    public static flush() {
        try {
            localStorage.removeItem(ACTIONS_KEY);
            localStorage.removeItem(TASKS_KEY);
            localStorage.removeItem(WORKFLOW_KEY);
            localStorage.removeItem(CURRENT_KEY);
        } catch (e) {
            console.error('Error flushing persistence layer:', e);
        }
    }
}
